import { IMailingAddressCreate, IMailingAddressWithShopifyId } from "@smartrr/shared/entities/MailingAddress";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { toast } from "react-toastify";

import { typedCustomerFrontendApi } from "@customer-app/shared/typedCustomerFrontendApi";
import { customerAsyncDispatch } from "@customer-app/state/customerAsyncDispatch";
import { CustomerThunkAction } from "@customer-app/state/typedCustomerRedux";

export const loadAddresses =
  (): CustomerThunkAction<
    // TS throwing build error if user action types are not specified
    "LOADING_ADDRESSES" | "ERROR_LOADING_ADDRESSES" | "LOADED_ADDRESSES"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.getReq("/addresses"),
      () =>
        dispatch({
          type: "LOADING_ADDRESSES",
          payload: undefined,
        }),
      () =>
        dispatch({
          type: "ERROR_LOADING_ADDRESSES",
          payload: {
            errorMessage: "error loading addresses",
          },
        }),
      addresses =>
        dispatch({
          type: "LOADED_ADDRESSES",
          payload: addresses,
        })
    );

export const createCustomerAddress =
  (
    newMailingAddress: IMailingAddressCreate,
    isDefault = false
  ): CustomerThunkAction<
    "CREATING_CUSTOMER_ADDRESS" | "CREATING_CUSTOMER_ADDRESS_ERROR" | "CREATED_CUSTOMER_ADDRESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.postReq("/address", {
        reqBody: {
          newMailingAddress,
          isDefault,
        },
      }),
      () =>
        dispatch({
          type: "CREATING_CUSTOMER_ADDRESS",
          payload: undefined,
        }),
      failure => {
        toast.error(
          // @ts-ignore https://github.com/microsoft/TypeScript/issues/34933
          typedLocalize("subscriptions.yourSubscriptions.orderOptionsModal.editAddressView.errorToast")
        );
        return dispatch({
          type: "CREATING_CUSTOMER_ADDRESS_ERROR",
          payload: {
            errorMessage: failure.message,
          },
        });
      },
      createdCustomerAddressRes => {
        toast(typedLocalize(`general.updatedToast`));
        return dispatch({
          type: "CREATED_CUSTOMER_ADDRESS",
          payload: {
            createdAddress: createdCustomerAddressRes.createdAddress,
          },
        });
      }
    );

export const updateCustomerAddress =
  (
    updatedMailingAddress: IMailingAddressWithShopifyId
  ): CustomerThunkAction<
    "UPDATING_CUSTOMER_ADDRESS" | "UPDATING_CUSTOMER_ADDRESS_ERROR" | "UPDATED_CUSTOMER_ADDRESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/address/:addressId", {
        params: {
          addressId: updatedMailingAddress.id,
        },
        reqBody: {
          updatedMailingAddress,
        },
      }),
      () =>
        dispatch({
          type: "UPDATING_CUSTOMER_ADDRESS",
          payload: undefined,
        }),
      error => {
        toast.error(
          typedLocalize("subscriptions.yourSubscriptions.orderOptionsModal.editAddressView.errorToast")
        );
        return dispatch({
          type: "UPDATING_CUSTOMER_ADDRESS_ERROR",
          payload: {
            errorMessage: `${error.message}`,
            address: null,
          },
        });
      },
      updatedCustomerAddressRes => {
        toast(typedLocalize(`general.updatedToast`));
        return dispatch({
          type: "UPDATED_CUSTOMER_ADDRESS",
          payload: {
            updatedAddress: updatedCustomerAddressRes.updatedAddress,
          },
        });
      }
    );

export const deleteCustomerAddress =
  (
    addressId: string
  ): CustomerThunkAction<
    "DELETING_CUSTOMER_ADDRESS" | "DELETING_CUSTOMER_ADDRESS_ERROR" | "DELETED_CUSTOMER_ADDRESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.deleteReq("/address/:addressId", {
        params: {
          addressId,
        },
      }),
      () =>
        dispatch({
          type: "DELETING_CUSTOMER_ADDRESS",
          payload: undefined,
        }),
      () =>
        dispatch({
          type: "DELETING_CUSTOMER_ADDRESS_ERROR",
          payload: {
            errorMessage: "Error deleting user address",
            address: null,
          },
        }),
      () =>
        dispatch({
          type: "DELETED_CUSTOMER_ADDRESS",
          payload: {
            deletedCustomerAddressId: addressId,
          },
        })
    );

// This method will ultimately update three address:
//    1. the defaultAddress of CustomerRelationshipOrm for the given customer (customer id provided by customerAsyncDispatch?)
//    2. the shippingAddress of CustomerPurchaseStateOrm for the given customer
//    3. the (shopify subscription contract's) contract.defaultAddress for all those CPSs affected by 2
//    4. the (shopify subscription contract's) contract.deliveryMethod.address for all those CPSs affected by 2
export const setDefaultAddress =
  (
    updatedMailingAddress: IMailingAddressWithShopifyId
  ): CustomerThunkAction<
    "SETTING_DEFAULT_CUSTOMER_ADDRESS" | "SETTING_DEFAULT_CUSTOMER_ADDRESS_ERROR" | "SET_DEFAULT_CUSTOMER_ADDRESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/default-address/:addressId", {
        params: {
          addressId: updatedMailingAddress.id,
        },
        reqBody: {
          updatedMailingAddress,
        },
      }),
      () =>
        dispatch({
          type: "SETTING_DEFAULT_CUSTOMER_ADDRESS",
          payload: undefined,
        }),
      () =>
        dispatch({
          type: "SETTING_DEFAULT_CUSTOMER_ADDRESS_ERROR",
          payload: {
            errorMessage: "Error saving user address",
            address: null,
          },
        }),
      defaultCustomerAddressRes =>
        dispatch({
          type: "SET_DEFAULT_CUSTOMER_ADDRESS",
          payload: {
            defaultCustomerAddress: defaultCustomerAddressRes.defaultAddress,
          },
        })
    );
